import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './SystemSettings.css';
import SvgIcon from '../../components/SvgIcon';
import CertificateTemplatesManager from '../../components/Admin/CertificateTemplatesManager';

const API_BASE = process.env.REACT_APP_API_URL || '';

const CATEGORIES = [
  { id: 'REGISTRATION_SLA', label: 'Registration & Vetting SLAs', icon: 'clipboard' },
  { id: 'LIFECYCLE_PDPA', label: 'Account Lifecycle & PDPA', icon: 'shield' },
  { id: 'ATTENDANCE_CERT', label: 'Attendance & Certification', icon: 'award' },
  { id: 'DASHBOARD_REPORTS', label: 'Dashboards & Reports', icon: 'barChart' }
];

export const SETTING_MODULE_MAP = {
  // ── 1. REGISTRATION & VETTING SLAS (Session Registration Page Module) ──
  REG_GOV_VETTING_SLA_HOURS: { module: 'Session Registration' },
  REG_SLA_WARNING_HOURS: { module: 'Session Registration' },
  REG_OVERDUE_ALERT_DAYS: { module: 'Session Registration' },
  REG_CRITICAL_ALERT_DAYS: { module: 'Session Registration' },
  REG_AUTO_REJECT_ENABLED: { module: 'Session Registration' },
  REG_AUTO_REJECT_DAYS: { module: 'Session Registration' },
  REG_PUBLIC_HOLIDAYS_JSON: { module: 'Session Registration' },

  // ── 2. ACCOUNT LIFECYCLE & DATA RETENTION (Account Registration Page Module) ──
  PDPA_INACTIVITY_DEACTIVATE_DAYS: { module: 'Account Registration' },
  PDPA_INACTIVITY_WARNING_NOTICE_DAYS: { module: 'Account Registration' },
  PDPA_PURGE_RETENTION_DAYS: { module: 'Account Registration' },
  PDPA_LIFECYCLE_SWEEP_MINUTES: { module: 'Account Registration' },

  // ── 3. ATTENDANCE & CERTIFICATION RULES (Trainer Dashboard & Session Registration Page Modules) ──
  ATTENDANCE_CODE_DEFAULT_VALIDITY_MINUTES: { module: 'Trainer Dashboard' },
  ATTENDANCE_CODE_ALLOWED_DURATIONS_JSON: { module: 'Trainer Dashboard' },
  CERT_ATTENDANCE_THRESHOLD_PERCENT: { module: 'Session Registration' },
  ENROLLMENT_BLACKOUT_THRESHOLD_COUNT: { module: 'Session Registration' },
  ENROLLMENT_BLACKOUT_DURATION_MONTHS: { module: 'Session Registration' },
  ENROLLMENT_POOR_ATTENDANCE_PERCENT: { module: 'Session Registration' },
  ENROLLMENT_RE_ENROLL_REQUIRES_APPROVAL: { module: 'Session Registration' },

  // ── 4. ADMIN DASHBOARD & REPORTING PARAMETERS (Dashboard & Reports Page Modules) ──
  DASHBOARD_WITHDRAWAL_LOOKAHEAD_DAYS: { module: 'Dashboard' },
  DASHBOARD_RECENT_ACTIVITY_DAYS: { module: 'Dashboard' },
  REPORT_DEFAULT_TIMEFRAME_PRESET: { module: 'Reports' }
};

export const SETTING_EXPLANATIONS = {
  REG_GOV_VETTING_SLA_HOURS: 'Specifies the government vetting approval SLA window in business working hours. Used dynamically by the SLA calculation engine (slaCalculator.js) to determine the exact approval deadline for civil service registrations, strictly excluding weekend days (Saturday and Sunday) and configured official public holidays.',
  REG_SLA_WARNING_HOURS: 'The advance warning threshold in business hours before a vetting SLA deadline expires. When remaining time until the SLA deadline falls below this threshold, registration and account update status badges transition to an amber warning (Due in < 1d) to alert administrators.',
  REG_OVERDUE_ALERT_DAYS: 'The pending registration aging threshold in calendar days. If a pending civil service registration has remained unreviewed past this duration, it is automatically aggregated into the daily administrative aging digest email sent to review officers.',
  REG_CRITICAL_ALERT_DAYS: 'The high-priority aging threshold in calendar days for elevating an unreviewed registration to Critical Overdue. Registrations exceeding this threshold trigger high-priority alerts on admin review queues. Must strictly exceed the overdue alert threshold.',
  REG_AUTO_REJECT_ENABLED: 'Master automation switch enabling or disabling the system from automatically rejecting stale, unreviewed civil service registrations. When enabled, registrations exceeding the configured auto-reject grace period are automatically marked Rejected and notification emails dispatched.',
  REG_AUTO_REJECT_DAYS: 'The maximum grace period in calendar days an unreviewed registration may remain pending before automated system rejection executes. Must strictly exceed the overdue alert threshold so officers have sufficient notice to review pending learners.',
  REG_PUBLIC_HOLIDAYS_JSON: 'An array of recognized federal and regional public holidays (format: YYYY-MM-DD). The dynamic SLA engine excludes all dates in this list when computing business working hours and vetting deadline timers. Administrators can manage single days or multiple-day holiday ranges.',
  PDPA_INACTIVITY_DEACTIVATE_DAYS: 'The dormancy threshold in calendar days since a user\'s last login or class attendance before their account is soft-deactivated (IsActive = 0). Soft-deactivated learners and trainers cannot log in until reactivated by an administrator.',
  PDPA_INACTIVITY_WARNING_NOTICE_DAYS: 'The advance notification window in calendar days prior to account deactivation. Configured to schedule warning emails to dormant users alerting them to log in before their account reaches the deactivation threshold. Must be strictly less than the deactivation threshold.',
  PDPA_PURGE_RETENTION_DAYS: 'The maximum legal data retention period in calendar days for deactivated accounts under PDPA compliance. Once an account has remained continuously deactivated for longer than this duration, the automated routine permanently and irreversibly purges the user\'s data from the database.',
  PDPA_LIFECYCLE_SWEEP_MINUTES: 'The recurrence interval in minutes at which the automated background timer runs to evaluate accounts against dormancy deactivation and permanent PDPA deletion thresholds. Controls the timer loop (schedulePdpaSweep) on the backend server.',
  ATTENDANCE_CODE_DEFAULT_VALIDITY_MINUTES: 'The default expiration lifespan in minutes assigned to newly generated session attendance check-in codes. Trainers generate these codes during live training classes for learners to check in.',
  ATTENDANCE_CODE_ALLOWED_DURATIONS_JSON: 'A JSON array specifying the permitted validity duration presets (in minutes) available to trainers when creating attendance check-in codes. Controls the duration selection buttons in the trainer console.',
  CERT_ATTENDANCE_THRESHOLD_PERCENT: 'The minimum overall attendance percentage required for a learner across all session segments of a course to qualify for completion certificate generation. The PDF certificate generation engine blocks issuing certificates if attendance is below this threshold.',
  ENROLLMENT_BLACKOUT_THRESHOLD_COUNT: 'The maximum number of completed course registrations with poor or zero attendance a learner can accumulate before an automated course enrollment suspension (blackout) is triggered against their account.',
  ENROLLMENT_BLACKOUT_DURATION_MONTHS: 'The suspension duration in calendar months applied to a learner\'s account upon reaching the blackout threshold count. During this period, the learner is barred from enrolling in any new courses unless granted an administrator override.',
  ENROLLMENT_POOR_ATTENDANCE_PERCENT: 'The percentage cutoff below which a finished registration is officially flagged as Poor/Incomplete Attendance toward the learner\'s blackout threshold count (e.g. attending less than 70% of classes).',
  ENROLLMENT_RE_ENROLL_REQUIRES_APPROVAL: 'Determines whether learners attempting to re-enroll in a course they previously failed, abandoned, or were withdrawn from require explicit administrative approval and override justification before enrollment is confirmed.',
  DASHBOARD_WITHDRAWAL_LOOKAHEAD_DAYS: 'The future lookahead window in calendar days from the current date. Used by executive dashboard analytics to filter and compute registration cancellation and withdrawal trends for upcoming training cohorts.',
  DASHBOARD_RECENT_ACTIVITY_DAYS: 'The lookback window in calendar days for aggregating rolling weekly approval counters, registration velocity, and operational statistics displayed on the primary Admin Dashboard overview cards.',
  REPORT_DEFAULT_TIMEFRAME_PRESET: 'The initial default timeframe filter preset (weekly, monthly, yearly, or custom) loaded when opening the CCoE Training & Class Reports console.'
};

/**
 * Determines the defined unit label (e.g. 'days', 'hours', 'minutes', '%')
 * based on setting metadata or key naming conventions.
 */
export const getSettingUnit = (setting) => {
  if (!setting) return '';
  if (setting.Unit) return setting.Unit;
  const key = setting.SettingKey || '';
  if (key.endsWith('_DAYS') || key.includes('_DAYS_')) return 'days';
  if (key.endsWith('_HOURS') || key.includes('_HOURS_')) return 'hours';
  if (key.endsWith('_MINUTES') || key.includes('_MINUTES_')) return 'minutes';
  if (key.endsWith('_PERCENT') || key.includes('_PERCENT_')) return '%';
  if (key.endsWith('_SECONDS') || key.includes('_SECONDS_')) return 'seconds';
  if (key.endsWith('_MONTHS') || key.includes('_MONTHS_')) return 'months';
  if (key.endsWith('_COUNT') || key.includes('_COUNT_')) return 'times';

  const desc = (setting.Description || '').toLowerCase();
  if (desc.includes(' month') || desc.includes('months')) return 'months';
  if (desc.includes(' day') || desc.includes('days')) return 'days';
  if (desc.includes(' hour') || desc.includes('hours')) return 'hours';
  if (desc.includes(' minute') || desc.includes('minutes')) return 'minutes';
  if (desc.includes('percent')) return '%';
  return '';
};

/**
 * Returns an array of 'YYYY-MM-DD' dates within [startDateStr, endDateStr] (inclusive).
 * Timezone-safe UTC calculations prevent day-shifting bugs.
 */
export const getDatesInRange = (startDateStr, endDateStr) => {
  if (!startDateStr) return [];
  if (!endDateStr || startDateStr === endDateStr) return [startDateStr];

  const [y1, m1, d1] = startDateStr.split('-').map(Number);
  const [y2, m2, d2] = endDateStr.split('-').map(Number);

  let start = new Date(Date.UTC(y1, m1 - 1, d1));
  let end = new Date(Date.UTC(y2, m2 - 1, d2));

  if (start > end) {
    const tmp = start;
    start = end;
    end = tmp;
  }

  const result = [];
  const current = new Date(start);
  while (current <= end) {
    const y = current.getUTCFullYear();
    const m = String(current.getUTCMonth() + 1).padStart(2, '0');
    const d = String(current.getUTCDate()).padStart(2, '0');
    result.push(`${y}-${m}-${d}`);
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return result;
};

/**
 * Specialized Holiday Range Picker for REG_PUBLIC_HOLIDAYS_JSON.
 * Allows choosing single dates or multi-day date ranges directly in an interactive calendar dropdown.
 */
export const HolidayRangeManager = ({ setting, currentVal, onChange, setToast }) => {
  const holidays = useMemo(() => {
    try {
      const parsed = typeof currentVal === 'string' ? JSON.parse(currentVal) : currentVal;
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }, [currentVal]);

  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [hoverDate, setHoverDate] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Initialize view month/year to today, or 2026 if 2026 dates exist in holidays
  const today = new Date();
  const [viewYear, setViewYear] = useState(() => {
    if (holidays.length > 0) {
      const yr = parseInt(holidays[0].slice(0, 4), 10);
      if (!isNaN(yr)) return yr;
    }
    return today.getFullYear();
  });
  const [viewMonth, setViewMonth] = useState(() => {
    if (holidays.length > 0) {
      const mo = parseInt(holidays[0].slice(5, 7), 10);
      if (!isNaN(mo)) return mo - 1;
    }
    return today.getMonth();
  });

  const containerRef = useRef(null);

  // Close calendar on click outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsCalendarOpen(false);
        setIsSelecting(false);
        setHoverDate(null);
      }
    };
    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isCalendarOpen]);

  const handleRemoveHoliday = (dateStr) => {
    const updated = holidays.filter((d) => d !== dateStr);
    onChange(setting.SettingKey, JSON.stringify(updated));
  };

  const selectedDates = useMemo(() => {
    return getDatesInRange(rangeStart, rangeEnd || rangeStart);
  }, [rangeStart, rangeEnd]);

  const handleAddHolidays = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!rangeStart) return;

    const datesToAdd = getDatesInRange(rangeStart, rangeEnd || rangeStart);
    if (datesToAdd.length === 0) return;

    const newlyAdded = datesToAdd.filter((d) => !holidays.includes(d));
    const alreadyExisting = datesToAdd.filter((d) => holidays.includes(d));

    if (newlyAdded.length === 0) {
      if (setToast) {
        setToast({
          type: 'info',
          message: datesToAdd.length === 1
            ? `${rangeStart} is already listed as a public holiday.`
            : `All dates in range ${rangeStart} to ${rangeEnd || rangeStart} are already configured.`
        });
      }
      return;
    }

    const updated = Array.from(new Set([...holidays, ...newlyAdded])).sort();
    onChange(setting.SettingKey, JSON.stringify(updated));

    if (setToast) {
      let msg = `Added ${newlyAdded.length} holiday${newlyAdded.length > 1 ? 's' : ''}`;
      if (datesToAdd.length > 1) {
        msg += ` (${rangeStart} to ${rangeEnd || rangeStart})`;
      } else {
        msg += ` (${rangeStart})`;
      }
      if (alreadyExisting.length > 0) {
        msg += ` (${alreadyExisting.length} already existed)`;
      }
      setToast({ type: 'success', message: msg });
    }

    setRangeStart('');
    setRangeEnd('');
    setIsSelecting(false);
    setHoverDate(null);
    setIsCalendarOpen(false);
  };

  const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const prevMonth = () => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  };

  const nextMonth = () => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  };

  const calendarDays = useMemo(() => {
    const totalDaysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const totalDaysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();
    const startDayOfWeek = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7; // Monday = 0

    const days = [];

    // Prev month padding
    const prevMonthNum = viewMonth === 0 ? 12 : viewMonth;
    const prevYearNum = viewMonth === 0 ? viewYear - 1 : viewYear;
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const dNum = totalDaysInPrevMonth - i;
      const dateStr = `${prevYearNum}-${String(prevMonthNum).padStart(2, '0')}-${String(dNum).padStart(2, '0')}`;
      days.push({ dateStr, dayNum: dNum, isCurrentMonth: false });
    }

    // Current month days
    const currentMonthNum = viewMonth + 1;
    for (let d = 1; d <= totalDaysInMonth; d++) {
      const dateStr = `${viewYear}-${String(currentMonthNum).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push({ dateStr, dayNum: d, isCurrentMonth: true });
    }

    // Next month padding to make full weeks
    const nextMonthNum = viewMonth === 11 ? 1 : viewMonth + 2;
    const nextYearNum = viewMonth === 11 ? viewYear + 1 : viewYear;
    let nextDay = 1;
    while (days.length % 7 !== 0 || days.length < 35) {
      const dateStr = `${nextYearNum}-${String(nextMonthNum).padStart(2, '0')}-${String(nextDay).padStart(2, '0')}`;
      days.push({ dateStr, dayNum: nextDay, isCurrentMonth: false });
      nextDay++;
    }

    return days;
  }, [viewYear, viewMonth]);

  const effectiveStart = useMemo(() => {
    if (!rangeStart) return null;
    if (isSelecting && hoverDate) {
      return hoverDate < rangeStart ? hoverDate : rangeStart;
    }
    if (rangeEnd) {
      return rangeEnd < rangeStart ? rangeEnd : rangeStart;
    }
    return rangeStart;
  }, [rangeStart, rangeEnd, isSelecting, hoverDate]);

  const effectiveEnd = useMemo(() => {
    if (!rangeStart) return null;
    if (isSelecting && hoverDate) {
      return hoverDate < rangeStart ? rangeStart : hoverDate;
    }
    if (rangeEnd) {
      return rangeEnd < rangeStart ? rangeStart : rangeEnd;
    }
    return rangeStart;
  }, [rangeStart, rangeEnd, isSelecting, hoverDate]);

  const handleCellClick = (dateStr) => {
    if (!isSelecting) {
      setRangeStart(dateStr);
      setRangeEnd('');
      setIsSelecting(true);
      setHoverDate(null);
    } else {
      if (dateStr < rangeStart) {
        setRangeEnd(rangeStart);
        setRangeStart(dateStr);
      } else {
        setRangeEnd(dateStr);
      }
      setIsSelecting(false);
      setHoverDate(null);
    }
  };

  const todayStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }, []);

  return (
    <div className="holiday-manager-root">
      {/* Holiday chips container */}
      <div className="holiday-chips-container" role="list">
        {holidays.map((h) => (
          <span key={h} className="holiday-chip" role="listitem">
            📅 {h}
            <button
              type="button"
              className="holiday-chip-remove"
              onClick={() => handleRemoveHoliday(h)}
              title={`Remove holiday ${h}`}
              aria-label={`Remove holiday ${h}`}
            >
              &times;
            </button>
          </span>
        ))}
        {holidays.length === 0 && (
          <span className="holiday-empty-text">No public holidays configured.</span>
        )}
      </div>

      {/* Date Range Calendar Selection */}
      <div className="holiday-range-wrapper" ref={containerRef}>
        <div className="holiday-add-row">
          <div
            className={`holiday-range-trigger ${isCalendarOpen ? 'active' : ''}`}
            onClick={() => setIsCalendarOpen((prev) => !prev)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsCalendarOpen((prev) => !prev);
              }
            }}
            aria-label="Holiday date range selection"
            aria-haspopup="dialog"
            aria-expanded={isCalendarOpen}
          >
            <span className="holiday-trigger-icon">📅</span>
            <span className="holiday-trigger-text">
              {rangeStart && rangeEnd && rangeStart !== rangeEnd
                ? `${rangeStart}  to  ${rangeEnd} (${selectedDates.length} days)`
                : rangeStart
                ? `${rangeStart} (choose end date or click add)`
                : 'Select holiday date or date range in calendar...'}
            </span>
            {rangeStart && (
              <span
                className="holiday-trigger-clear"
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setRangeStart('');
                  setRangeEnd('');
                  setIsSelecting(false);
                  setHoverDate(null);
                }}
                title="Clear selected range"
                aria-label="Clear date selection"
              >
                &times;
              </span>
            )}
            <span className="holiday-trigger-chevron">
              {isCalendarOpen ? '▲' : '▼'}
            </span>
          </div>

          <button
            type="button"
            className="holiday-add-btn"
            onClick={handleAddHolidays}
            disabled={!rangeStart}
          >
            {selectedDates.length > 1
              ? `+ Add ${selectedDates.length} Holidays`
              : '+ Add Holiday'}
          </button>
        </div>

        {/* Dropdown Calendar Date Range Picker */}
        {isCalendarOpen && (
          <div className="holiday-calendar-dropdown" role="dialog" aria-label="Holiday Calendar Date Range Selection">
            <div className="holiday-cal-header">
              <button
                type="button"
                className="holiday-cal-nav-btn"
                onClick={prevMonth}
                title="Previous month"
                aria-label="Previous month"
              >
                &lsaquo;
              </button>

              <div className="holiday-cal-month-year-selects">
                <select
                  className="holiday-cal-select"
                  value={viewMonth}
                  onChange={(e) => setViewMonth(parseInt(e.target.value, 10))}
                  aria-label="Select month"
                >
                  {MONTH_NAMES.map((name, idx) => (
                    <option key={name} value={idx}>{name}</option>
                  ))}
                </select>

                <select
                  className="holiday-cal-select"
                  value={viewYear}
                  onChange={(e) => setViewYear(parseInt(e.target.value, 10))}
                  aria-label="Select year"
                >
                  {Array.from({ length: 11 }, (_, i) => 2022 + i).map((yr) => (
                    <option key={yr} value={yr}>{yr}</option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                className="holiday-cal-nav-btn"
                onClick={nextMonth}
                title="Next month"
                aria-label="Next month"
              >
                &rsaquo;
              </button>
            </div>

            <div className="holiday-cal-instructions">
              {isSelecting
                ? '👉 Click end date to finish range (or click same date for 1 day)'
                : rangeStart && rangeEnd
                ? `Range: ${rangeStart} to ${rangeEnd} (${selectedDates.length} days selected)`
                : 'Click a start date to choose holiday range'}
            </div>

            {/* Days of week */}
            <div className="holiday-cal-weekdays">
              <span>Mo</span>
              <span>Tu</span>
              <span>We</span>
              <span>Th</span>
              <span>Fr</span>
              <span>Sa</span>
              <span>Su</span>
            </div>

            {/* Calendar grid */}
            <div className="holiday-cal-grid" onMouseLeave={() => setHoverDate(null)}>
              {calendarDays.map((day, idx) => {
                const isStart = day.dateStr === effectiveStart;
                const isEnd = day.dateStr === effectiveEnd;
                const inRange = Boolean(
                  effectiveStart &&
                  effectiveEnd &&
                  day.dateStr >= effectiveStart &&
                  day.dateStr <= effectiveEnd
                );
                const isHoliday = holidays.includes(day.dateStr);
                const isToday = day.dateStr === todayStr;
                const isWeekStart = idx % 7 === 0;
                const isWeekEnd = idx % 7 === 6;

                let cellClass = 'holiday-cal-cell';
                if (!day.isCurrentMonth) cellClass += ' other-month';
                if (inRange) cellClass += ' in-range';
                if (isStart) cellClass += ' range-start';
                if (isEnd) cellClass += ' range-end';
                if (isWeekStart) cellClass += ' week-start';
                if (isWeekEnd) cellClass += ' week-end';
                if (isToday) cellClass += ' is-today';
                if (isHoliday) cellClass += ' is-holiday';

                return (
                  <button
                    type="button"
                    key={day.dateStr}
                    className={cellClass}
                    onClick={() => handleCellClick(day.dateStr)}
                    onMouseEnter={() => {
                      if (isSelecting) setHoverDate(day.dateStr);
                    }}
                    title={`${day.dateStr}${isHoliday ? ' (Existing Holiday)' : ''}`}
                    aria-label={`Date ${day.dateStr}${isHoliday ? ' (Existing Holiday)' : ''}`}
                  >
                    <span className="cal-day-num">{day.dayNum}</span>
                    {isHoliday && <span className="cal-holiday-dot" title="Existing holiday" />}
                  </button>
                );
              })}
            </div>

            {/* Footer with manual From/To inputs and quick action buttons */}
            <div className="holiday-cal-footer">
              <div className="holiday-cal-from-to">
                <div className="holiday-input-subgroup">
                  <label htmlFor="cal-from-date">From:</label>
                  <input
                    id="cal-from-date"
                    type="date"
                    className="holiday-date-subinput"
                    value={rangeStart}
                    onChange={(e) => {
                      setRangeStart(e.target.value);
                      setIsSelecting(false);
                    }}
                    aria-label="From date"
                  />
                </div>
                <span className="holiday-arrow-sep">→</span>
                <div className="holiday-input-subgroup">
                  <label htmlFor="cal-to-date">To:</label>
                  <input
                    id="cal-to-date"
                    type="date"
                    className="holiday-date-subinput"
                    value={rangeEnd}
                    onChange={(e) => {
                      setRangeEnd(e.target.value);
                      setIsSelecting(false);
                    }}
                    aria-label="To date"
                  />
                </div>
              </div>

              <div className="holiday-cal-footer-actions">
                <button
                  type="button"
                  className="holiday-cal-action-btn"
                  onClick={() => {
                    setRangeStart(todayStr);
                    setRangeEnd(todayStr);
                    setIsSelecting(false);
                    setHoverDate(null);
                    setViewYear(today.getFullYear());
                    setViewMonth(today.getMonth());
                  }}
                >
                  Today
                </button>
                <button
                  type="button"
                  className="holiday-cal-action-btn"
                  onClick={() => {
                    setRangeStart('');
                    setRangeEnd('');
                    setIsSelecting(false);
                    setHoverDate(null);
                  }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="holiday-cal-action-btn primary"
                  onClick={() => {
                    setIsCalendarOpen(false);
                    setIsSelecting(false);
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const SystemSettings = ({ user }) => {
  const [settingsList, setSettingsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [activeTab, setActiveTab] = useState('REGISTRATION_SLA');

  // Dirty state tracking: key -> editedValue
  const [editedSettings, setEditedSettings] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  // Clear toast after timeout
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Fetch settings from API
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        headers: {
          'x-admin-role': user?.role || user?.Role || 'Admin',
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (res.ok && data.status === 'success' && Array.isArray(data.flat)) {
        setSettingsList(data.flat);
        setEditedSettings({});
      } else {
        setFetchError(data.message || 'Failed to retrieve application settings.');
      }
    } catch (err) {
      setFetchError('Network error connecting to administrative API.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Helper to get current effective value for a key (edited or baseline)
  const getValue = useCallback((setting) => {
    if (editedSettings[setting.SettingKey] !== undefined) {
      return editedSettings[setting.SettingKey];
    }
    return setting.SettingValue;
  }, [editedSettings]);

  // Handle field change
  const handleChange = (key, value) => {
    setEditedSettings((prev) => {
      const orig = settingsList.find((s) => s.SettingKey === key);
      if (orig && String(orig.SettingValue) === String(value)) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: value };
    });
  };

  // Discard all changes
  const handleDiscard = () => {
    setEditedSettings({});
  };

  // Reset single key to DefaultValue
  const handleResetSingle = async (setting) => {
    if (!window.confirm(`Restore '${setting.SettingKey}' to its default factory value (${setting.DefaultValue})?`)) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/admin/settings/reset/${encodeURIComponent(setting.SettingKey)}`, {
        method: 'POST',
        headers: {
          'x-admin-role': user?.role || user?.Role || 'Admin',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adminId: user?.id || user?.AdminID || null,
          adminName: user?.fullName || user?.name || 'Administrator',
          adminRole: user?.role || 'Admin'
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', message: data.message });
        fetchSettings();
      } else {
        setToast({ type: 'error', message: data.message || 'Failed to reset setting.' });
      }
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to communicate with server.' });
    }
  };

  // Cross-field validation checks before saving
  const validationAlert = useMemo(() => {
    const getValInt = (key, def) => {
      const s = settingsList.find((x) => x.SettingKey === key);
      const raw = editedSettings[key] !== undefined ? editedSettings[key] : (s?.SettingValue ?? def);
      return parseInt(raw, 10);
    };

    const getValBool = (key, def) => {
      const s = settingsList.find((x) => x.SettingKey === key);
      const raw = editedSettings[key] !== undefined ? editedSettings[key] : (s?.SettingValue ?? def);
      return String(raw).toLowerCase() === 'true';
    };

    const overdueDays = getValInt('REG_OVERDUE_ALERT_DAYS', 5);
    const criticalDays = getValInt('REG_CRITICAL_ALERT_DAYS', 7);
    const autoReject = getValBool('REG_AUTO_REJECT_ENABLED', false);
    const autoRejectDays = getValInt('REG_AUTO_REJECT_DAYS', 14);

    if (criticalDays <= overdueDays) {
      return `Critical alert threshold (${criticalDays}d) must be greater than overdue alert threshold (${overdueDays}d).`;
    }

    if (autoReject && autoRejectDays <= overdueDays) {
      return `Auto-rejection grace period (${autoRejectDays}d) must strictly exceed the overdue alert threshold (${overdueDays}d) so administrators receive alert digests first.`;
    }

    const inactDays = getValInt('PDPA_INACTIVITY_DEACTIVATE_DAYS', 365);
    const warnDays = getValInt('PDPA_INACTIVITY_WARNING_NOTICE_DAYS', 30);
    if (warnDays >= inactDays) {
      return `Advance warning window (${warnDays}d) cannot be equal to or greater than dormancy threshold (${inactDays}d).`;
    }

    return null;
  }, [editedSettings, settingsList]);

  // Execute Save Changes
  const executeSave = async () => {
    setIsSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'x-admin-role': user?.role || user?.Role || 'Admin',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          settings: editedSettings,
          adminId: user?.id || user?.AdminID || null,
          adminName: user?.fullName || user?.name || 'Administrator',
          adminRole: user?.role || 'Admin'
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', message: data.message });
        setEditedSettings({});
        fetchSettings();
      } else {
        setToast({ type: 'error', message: data.message || 'Validation error while saving settings.' });
      }
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to update application settings.' });
    } finally {
      setIsSaving(false);
      setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
    }
  };

  // Submit button clicked
  const handleSaveClick = () => {
    if (validationAlert) {
      setToast({ type: 'error', message: validationAlert });
      return;
    }

    // Check for high-impact destructive changes
    const isEnablingAutoReject = editedSettings.REG_AUTO_REJECT_ENABLED === 'true';
    const purgeRetention = parseInt(editedSettings.PDPA_PURGE_RETENTION_DAYS || '365', 10);

    if (isEnablingAutoReject) {
      setConfirmModal({
        isOpen: true,
        title: 'Confirm Automated Rejection Activation',
        message: 'You are enabling automated auto-rejection of pending registrations. Unreviewed registrations exceeding the configured grace period will be automatically marked Rejected and notification emails dispatched to learners. Are you sure?',
        onConfirm: executeSave
      });
      return;
    }

    if (purgeRetention < 90) {
      setConfirmModal({
        isOpen: true,
        title: 'Confirm Reduced Retention Period',
        message: `You are configuring permanent account purge to ${purgeRetention} days. Deactivated user data cannot be recovered after this duration. Confirm?`,
        onConfirm: executeSave
      });
      return;
    }

    executeSave();
  };

  // Filtered settings for current tab
  const filteredSettings = useMemo(() => {
    return settingsList.filter((s) => s.Category === activeTab);
  }, [settingsList, activeTab]);

  const dirtyCount = Object.keys(editedSettings).length;

  return (
    <div className="system-settings-container" id="system-settings-console">
      {/* Toast Notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          padding: '12px 20px',
          borderRadius: '8px',
          backgroundColor: toast.type === 'error' ? '#ef4444' : '#10b981',
          color: '#ffffff',
          fontWeight: 600,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>{toast.type === 'error' ? '⚠️' : '✅'}</span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="settings-header">
        <div className="settings-header-left">
          <h1>
            <SvgIcon name="settings" size={26} />
            Application Settings
          </h1>
          <p className="settings-subtitle">
            Manage operational parameters, SLAs, data retention thresholds, and dashboard lookahead metrics in real-time.
          </p>
        </div>
      </div>

      {/* Cross-field Validation Error Banner */}
      {validationAlert && (
        <div className="settings-alert-banner">
          <SvgIcon name="alertTriangle" size={20} />
          <span>{validationAlert}</span>
        </div>
      )}

      {/* Category Tabs */}
      <div className="settings-tabs-wrapper" role="tablist">
        {CATEGORIES.map((cat) => {
          const count = settingsList.filter((s) => s.Category === cat.id).length;
          return (
            <button
              key={cat.id}
              role="tab"
              aria-selected={activeTab === cat.id}
              className={`settings-tab-btn ${activeTab === cat.id ? 'active' : ''}`}
              onClick={() => setActiveTab(cat.id)}
            >
              <SvgIcon name={cat.icon} size={18} />
              <span>{cat.label}</span>
              <span className="tab-count-badge">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Content Body */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
          <p>Loading configuration parameters from database...</p>
        </div>
      ) : fetchError ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#dc2626' }}>
          <p>⚠️ {fetchError}</p>
          <button className="setting-reset-btn" onClick={fetchSettings} style={{ margin: '1rem auto' }}>
            Retry
          </button>
        </div>
      ) : filteredSettings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
          <p>No configuration settings found matching your criteria.</p>
        </div>
      ) : (
        <div className="settings-grid">
          {filteredSettings.map((setting) => {
            const currentVal = getValue(setting);
            const isDirty = editedSettings[setting.SettingKey] !== undefined;
            const unit = getSettingUnit(setting);
            const moduleInfo = SETTING_MODULE_MAP[setting.SettingKey];
            const descriptionText = (setting.Description && setting.Description.length > 80)
              ? setting.Description
              : (SETTING_EXPLANATIONS[setting.SettingKey] || setting.Description);

            return (
              <div
                key={setting.SettingKey}
                className={`setting-card ${isDirty ? 'is-dirty' : ''}`}
              >
                <div className="setting-card-header">
                  <div className="setting-title-group">
                    <div className="setting-title-row">
                      <span className="setting-title">
                        {setting.SettingKey.replace(/_/g, ' ')}
                      </span>
                      {moduleInfo && (
                        <span className="setting-module-badge" title={`Directly affects ${moduleInfo.module} page module`}>
                          {moduleInfo.module}
                        </span>
                      )}
                    </div>
                    <span className="setting-key-code">{setting.SettingKey}</span>
                  </div>

                  <div className="setting-meta-actions">
                    <span className="setting-default-tag">
                      Factory: {setting.DefaultValue}
                    </span>
                    <button
                      type="button"
                      className="setting-reset-btn"
                      onClick={() => handleResetSingle(setting)}
                      title="Revert to baseline factory value"
                    >
                      <SvgIcon name="refresh" size={14} />
                      Reset
                    </button>
                  </div>
                </div>

                <p className="setting-description">{descriptionText}</p>

                {/* Control inputs depending on DataType */}
                <div className="setting-control-row">
                  {/* Integer Control */}
                  {setting.DataType === 'Integer' && (
                    <div className="setting-input-wrapper">
                      <input
                        type="number"
                        className="setting-number-input"
                        value={currentVal}
                        min={setting.MinValue !== null ? setting.MinValue : undefined}
                        max={setting.MaxValue !== null ? setting.MaxValue : undefined}
                        onChange={(e) => handleChange(setting.SettingKey, e.target.value)}
                        aria-label={setting.SettingKey}
                      />
                      {unit && <span className="setting-unit-label">{unit}</span>}
                      <span className="setting-bounds-hint">
                        {setting.MinValue !== null && setting.MaxValue !== null
                          ? `(Min: ${setting.MinValue}, Max: ${setting.MaxValue})`
                          : ''}
                      </span>
                    </div>
                  )}

                  {/* Boolean Toggle */}
                  {setting.DataType === 'Boolean' && (() => {
                    const isBoolTrue = String(currentVal).toLowerCase() === 'true' || String(currentVal) === '1';
                    return (
                      <div
                        className="setting-toggle-wrapper"
                        role="switch"
                        aria-checked={isBoolTrue}
                        onClick={() => {
                          const next = isBoolTrue
                            ? (String(currentVal) === '1' ? '0' : 'false')
                            : (String(currentVal) === '0' ? '1' : 'true');
                          handleChange(setting.SettingKey, next);
                        }}
                      >
                        <div className={`setting-toggle-track ${isBoolTrue ? 'checked' : ''}`}>
                          <div className="setting-toggle-thumb" />
                        </div>
                        <span className="setting-toggle-status">
                          {isBoolTrue ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    );
                  })()}

                  {/* String Dropdown / Input */}
                  {setting.DataType === 'String' && (
                    <div className="setting-input-wrapper">
                      {setting.SettingKey === 'REPORT_DEFAULT_TIMEFRAME_PRESET' ? (
                        <select
                          className="setting-select-dropdown"
                          value={currentVal}
                          onChange={(e) => handleChange(setting.SettingKey, e.target.value)}
                          aria-label={setting.SettingKey}
                        >
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="yearly">Yearly</option>
                          <option value="custom">Custom</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="settings-search-input"
                          value={currentVal}
                          onChange={(e) => handleChange(setting.SettingKey, e.target.value)}
                        />
                      )}
                    </div>
                  )}

                  {/* JSON Editor (Specialized for Holidays and Allowed Durations) */}
                  {setting.DataType === 'JSON' && (
                    <div style={{ width: '100%' }}>
                      {setting.SettingKey === 'REG_PUBLIC_HOLIDAYS_JSON' && (
                        <HolidayRangeManager
                          setting={setting}
                          currentVal={currentVal}
                          onChange={handleChange}
                          setToast={setToast}
                        />
                      )}

                      {setting.SettingKey === 'ATTENDANCE_CODE_ALLOWED_DURATIONS_JSON' && (() => {
                        let durations = [10, 20, 30];
                        try {
                          durations = typeof currentVal === 'string' ? JSON.parse(currentVal) : currentVal;
                        } catch (_) {}

                        return (
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            {[5, 10, 15, 20, 30, 45, 60].map((dur) => {
                              const isChecked = durations.includes(dur);
                              return (
                                <button
                                  type="button"
                                  key={dur}
                                  style={{
                                    minHeight: '44px',
                                    padding: '0.4rem 0.9rem',
                                    borderRadius: '6px',
                                    border: isChecked ? '1px solid #2563eb' : '1px solid #cbd5e1',
                                    backgroundColor: isChecked ? '#eff6ff' : '#ffffff',
                                    color: isChecked ? '#1d4ed8' : '#475569',
                                    fontWeight: 700,
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => {
                                    const next = isChecked
                                      ? durations.filter((d) => d !== dur)
                                      : [...durations, dur].sort((a, b) => a - b);
                                    if (next.length > 0) {
                                      handleChange(setting.SettingKey, JSON.stringify(next));
                                    }
                                  }}
                                >
                                  {dur} mins {isChecked ? '✓' : ''}
                                </button>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {setting.UpdatedAt && (
                    <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                      Last updated: {setting.UpdatedAt} {setting.UpdatedByName ? `by ${setting.UpdatedByName}` : ''}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Certificate Templates Section under Attendance & Certification */}
      {activeTab === 'ATTENDANCE_CERT' && !loading && (
        <div style={{ marginTop: '2.5rem' }}>
          <CertificateTemplatesManager user={user} />
        </div>
      )}

      {/* Sticky Bottom Action Bar when edits are dirty */}
      {dirtyCount > 0 && (
        <div className="settings-sticky-bar" role="region" aria-label="Unsaved settings actions">
          <div className="sticky-bar-left">
            <div className="dirty-indicator-dot" />
            <span className="sticky-bar-text">
              {dirtyCount} unsaved configuration change{dirtyCount > 1 ? 's' : ''}
            </span>
          </div>

          <div className="sticky-bar-actions">
            <button
              type="button"
              className="sticky-discard-btn"
              onClick={handleDiscard}
              disabled={isSaving}
            >
              Discard Changes
            </button>
            <button
              type="button"
              className="sticky-save-btn"
              onClick={handleSaveClick}
              disabled={isSaving}
            >
              {isSaving ? 'Applying...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* High-Impact Destructive Confirmation Modal */}
      {confirmModal.isOpen && (
        <div className="settings-modal-backdrop" role="dialog" aria-modal="true">
          <div className="settings-modal-card">
            <h3>{confirmModal.title}</h3>
            <p>{confirmModal.message}</p>
            <div className="settings-modal-actions">
              <button
                type="button"
                className="modal-cancel-btn"
                onClick={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-confirm-btn"
                onClick={confirmModal.onConfirm}
              >
                Confirm & Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettings;
